import { useSyncExternalStore } from "react";

// Motion and visibility helpers shared by every animated HUD piece.
// DESIGN.md: reduced motion shows final states (no rotation, scramble, boot,
// scan line), and anything that loops pauses while the page is hidden.

const mq: MediaQueryList | null =
  typeof window !== "undefined" && typeof window.matchMedia === "function"
    ? window.matchMedia("(prefers-reduced-motion: reduce)")
    : null;

// A per-device switch in Settings also stops HUD motion (WCAG 2.2.2: moving
// content needs a way to pause it, not only the OS setting).
const MOTION_KEY = "br.motion";
let userReduced = readUserReduced();
const motionListeners = new Set<() => void>();

function readUserReduced(): boolean {
  try {
    return window.localStorage.getItem(MOTION_KEY) === "reduced";
  } catch {
    return false;
  }
}

function applyMotionAttr(): void {
  if (typeof document !== "undefined") document.documentElement.toggleAttribute("data-reduced-motion", userReduced);
}

export function setUserReducedMotion(on: boolean): void {
  userReduced = on;
  try {
    if (on) window.localStorage.setItem(MOTION_KEY, "reduced");
    else window.localStorage.removeItem(MOTION_KEY);
  } catch {
    // Storage blocked: applies to this page only.
  }
  applyMotionAttr();
  motionListeners.forEach((l) => l());
}

export function userReducedMotion(): boolean {
  return userReduced;
}

/** True when the OS asks for reduced motion. */
export function systemReducedMotion(): boolean {
  return !!mq?.matches;
}

export function prefersReducedMotion(): boolean {
  return !!mq?.matches || userReduced;
}

export function subscribeReducedMotion(cb: () => void): () => void {
  mq?.addEventListener("change", cb);
  motionListeners.add(cb);
  return () => {
    mq?.removeEventListener("change", cb);
    motionListeners.delete(cb);
  };
}

export function useReducedMotion(): boolean {
  return useSyncExternalStore(subscribeReducedMotion, prefersReducedMotion, () => false);
}

export function useMotionSettings(): { user: boolean; system: boolean } {
  const user = useSyncExternalStore(subscribeReducedMotion, userReducedMotion, () => false);
  const system = useSyncExternalStore(subscribeReducedMotion, systemReducedMotion, () => false);
  return { user, system };
}

function pageHidden(): boolean {
  return typeof document !== "undefined" && document.visibilityState === "hidden";
}

function subscribeVisibility(cb: () => void): () => void {
  document.addEventListener("visibilitychange", cb);
  return () => document.removeEventListener("visibilitychange", cb);
}

export function usePageHidden(): boolean {
  return useSyncExternalStore(subscribeVisibility, pageHidden, () => false);
}

/**
 * Mirrors page visibility onto <html data-hidden> so one CSS rule pauses every
 * CSS rotation, pulse and scan line while the tab is in the background.
 */
export function installVisibilityFlag(): void {
  if (typeof document === "undefined") return;
  const root = document.documentElement;
  applyMotionAttr();
  const sync = () => root.toggleAttribute("data-hidden", pageHidden());
  sync();
  document.addEventListener("visibilitychange", sync);
}
